// Receive-only Radio presence: keeps the signed-in user on their site's Radio
// channel while the app is open, so incoming transmissions play automatically
// without holding anything. Reuses useRadioLive / RadioSession unchanged.
import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { useRadioContext } from "@/hooks/useRadioContext";
import { RadioLiveContext } from "@/hooks/useRadioLive";
import { useRadioController } from "@/hooks/useRadioController";
import { resolveSiteRadioChannel } from "@/lib/radio";
import { Button } from "@/components/ui/button";
import { Volume2 } from "lucide-react";

export function RadioAutoListener({ children }: { children: ReactNode }) {
  const { user, currentOrg } = useAuth() as any;
  const { activeSite } = useRadioContext();
  const [error, setError] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const orgId = currentOrg?.id ?? null;
  const siteId = activeSite?.id ?? null;

  useEffect(() => {
    setChannelId(null);
    setError(null);
    if (!user || !orgId || !siteId) return;
    let cancelled = false;
    void resolveSiteRadioChannel(orgId, siteId)
      .then((id) => !cancelled && setChannelId(id))
      .catch(() => !cancelled && setError("Radio connection unavailable"));
    return () => {
      cancelled = true;
    };
  }, [user, orgId, siteId]);

  const radio = useRadioController({
    channelId,
    siteId,
  });
  return (
    <RadioLiveContext.Provider value={radio}>
      {children}
      {radio.playbackBlocked && (
        <div className="fixed left-1/2 top-20 z-50 -translate-x-1/2">
          <Button onClick={() => void radio.enableAudio()} size="sm">
            <Volume2 className="mr-2 h-4 w-4" /> Enable Radio audio
          </Button>
        </div>
      )}
      {(error || radio.state.error?.kind === "connection") && (
        <div role="status" className="fixed left-1/2 top-20 z-50 flex -translate-x-1/2 items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs text-destructive">
          {error ?? radio.state.error?.message}
          <Button variant="ghost" size="sm" onClick={() => { setError(null); radio.reconnect(); }}>Retry</Button>
        </div>
      )}
    </RadioLiveContext.Provider>
  );
}
